import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from 'src/app/shared/page-layout/page-search-panel.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { Party } from 'src/app/party/party.model';
import { PartyService } from 'src/app/party/party.service';
import { PartyType } from 'src/app/party/party-type.enum';
import { BankAccount } from '../../bank-account/bank-account.model';
import { BankAccountService } from '../../bank-account/bank-account.service';

@Component({
    selector: 'app-banking-bank-accounts',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ButtonModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent
    ],
    template: `
        <app-page-shell>
            <app-page-hero
                eyebrow="Banking setup"
                title="Bank Accounts"
                subtitle="Manage company, customer, vendor, employee, and operating bank accounts used by payments and reconciliation.">
                <div actions>
                    <p-button label="Banks" icon="pi pi-building-columns" severity="secondary" [outlined]="true" routerLink="/accounts/enterprise/banking/banks"></p-button>
                    <p-button label="New Bank Account" icon="pi pi-plus" routerLink="/accounts/enterprise/banking/bank-accounts/new"></p-button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-search-panel
                title="Bank account filters"
                [activeFilters]="activeFilterCount"
                [resultText]="resultText"
                (search)="loadAccounts()"
                (clear)="clearFilters()">
                <div class="filter-grid">
                    <label>
                        <span>Owner type</span>
                        <p-select [options]="ownerTypes" [(ngModel)]="ownerType" optionLabel="label" optionValue="value" (onChange)="onOwnerTypeChange()" appendTo="body"></p-select>
                    </label>
                    <label>
                        <span>Owner</span>
                        <p-select [options]="partyOptions" [(ngModel)]="selectedOwner" optionLabel="entityName" [filter]="true" [showClear]="false" (onFilter)="searchParties($event.filter)" appendTo="body"></p-select>
                    </label>
                    <label>
                        <span>Search</span>
                        <input pInputText [(ngModel)]="searchText" placeholder="Account, IBAN, number, bank" (keyup.enter)="loadAccounts()">
                    </label>
                    <label>
                        <span>Currency</span>
                        <p-select [options]="currencies" [(ngModel)]="currencyCode" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body"></p-select>
                    </label>
                    <label>
                        <span>Reconciliation</span>
                        <p-select [options]="reconciliationOptions" [(ngModel)]="reconciliationEnabled" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body"></p-select>
                    </label>
                </div>
            </app-page-search-panel>

            <app-page-data-panel title="Bank account directory" [subtitle]="selectedOwnerLabel" [resultText]="resultText">
                <p-table
                    [value]="filteredAccounts"
                    [loading]="loading"
                    [paginator]="true"
                    [rows]="15"
                    [rowsPerPageOptions]="[15, 30, 50]"
                    responsiveLayout="stack"
                    styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Account</th>
                            <th>Bank</th>
                            <th>Owner</th>
                            <th>Currency</th>
                            <th>GL Link</th>
                            <th>Reconciliation</th>
                            <th class="actions-column">Actions</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-account>
                        <tr>
                            <td>
                                <div class="cell-stack">
                                    <strong>{{ account.accountName || 'Unnamed account' }}</strong>
                                    <span>{{ account.accountNumber || 'No account number' }}</span>
                                    <small>{{ account.iban || 'No IBAN' }}</small>
                                </div>
                            </td>
                            <td>
                                <div class="bank-cell">
                                    @if (account.bank?.logoUrl) {
                                        <img [src]="account.bank.logoUrl" alt="">
                                    }
                                    <div class="cell-stack">
                                        <strong>{{ account.bank?.name || 'Unassigned bank' }}</strong>
                                        <small>{{ account.bank?.swiftCode || 'No SWIFT' }}</small>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="cell-stack">
                                    <strong>{{ ownerLabel(account) }}</strong>
                                    <small>{{ ownerTypeLabel(account) }}</small>
                                </div>
                            </td>
                            <td>{{ account.currencyCode || '-' }}</td>
                            <td>
                                <div class="cell-stack">
                                    <strong>{{ account.account?.accountName || 'Missing GL account' }}</strong>
                                    <small>{{ account.account?.accountCode || '-' }}</small>
                                </div>
                            </td>
                            <td>
                                <div class="cell-stack">
                                    <p-tag [value]="account.reconciliationEnabled ? 'Enabled' : 'Disabled'" [severity]="account.reconciliationEnabled ? 'success' : 'secondary'"></p-tag>
                                    <small>Last reconciled: {{ account.lastReconciledDate || 'Never' }}</small>
                                </div>
                            </td>
                            <td class="actions-column">
                                <div class="row-actions">
                                    <p-button icon="pi pi-eye" [text]="true" [rounded]="true" [routerLink]="['/accounts/enterprise/banking/bank-accounts', account.id]"></p-button>
                                    <p-button icon="pi pi-pencil" [text]="true" [rounded]="true" severity="secondary" [routerLink]="['/accounts/enterprise/banking/bank-accounts', account.id, 'edit']"></p-button>
                                    <p-button icon="pi pi-trash" [text]="true" [rounded]="true" severity="danger" (onClick)="deleteAccount(account)"></p-button>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="7">
                                <div class="empty-state">
                                    <i class="pi pi-wallet"></i>
                                    <strong>No bank accounts match the selected owner and filters.</strong>
                                    <span>Create a bank account or switch the owner filter.</span>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .filter-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1rem;
        }

        label {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            color: #475569;
            font-weight: 700;
        }

        label span {
            font-size: 0.82rem;
        }

        :host ::ng-deep .p-select,
        :host ::ng-deep .p-inputtext {
            width: 100%;
        }

        .cell-stack {
            display: flex;
            flex-direction: column;
            gap: 0.2rem;
        }

        .cell-stack small,
        .cell-stack span {
            color: #64748b;
        }

        .bank-cell,
        .row-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .bank-cell img {
            width: 34px;
            height: 34px;
            border-radius: 50%;
            object-fit: cover;
            background: #f1f5f9;
        }

        .actions-column {
            width: 9rem;
            text-align: right;
        }

        .row-actions {
            justify-content: flex-end;
        }

        .empty-state {
            display: flex;
            align-items: center;
            flex-direction: column;
            gap: 0.5rem;
            padding: 2rem;
            color: #64748b;
            text-align: center;
        }

        .empty-state i {
            color: #0f766e;
            font-size: 2rem;
        }
    `]
})
export class BankAccountsComponent implements OnInit {
    private readonly bankAccountService = inject(BankAccountService);
    private readonly partyService = inject(PartyService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly router = inject(Router);

    readonly ownerTypes = Object.values(PartyType).map(value => ({ label: this.formatOwnerType(value), value }));
    readonly currencies = ['SAR', 'USD', 'EUR', 'AED', 'BHD', 'KWD', 'QAR'].map(value => ({ label: value, value }));
    readonly reconciliationOptions = [
        { label: 'Enabled', value: true },
        { label: 'Disabled', value: false }
    ];

    ownerType: PartyType = PartyType.Company;
    partyOptions: Party[] = [];
    selectedOwner?: Party;
    searchText = '';
    currencyCode?: string;
    reconciliationEnabled?: boolean;
    accounts: BankAccount[] = [];
    loading = false;
    totalCount = 0;

    ngOnInit(): void {
        this.resetDefaultOwner();
        this.loadParties();
        this.loadAccounts();
    }

    get filteredAccounts(): BankAccount[] {
        return this.accounts.filter(account => {
            if (this.currencyCode && account.currencyCode !== this.currencyCode) return false;
            if (this.reconciliationEnabled !== undefined && Boolean(account.reconciliationEnabled) !== this.reconciliationEnabled) return false;
            return true;
        });
    }

    get metrics(): PageMetric[] {
        const rows = this.filteredAccounts;
        const enabled = rows.filter(account => account.reconciliationEnabled).length;
        const missingGl = rows.filter(account => !account.account?.accountId).length;
        const missingAdjustment = rows.filter(account => account.reconciliationEnabled && (
            !account.defaultSuspenseAccountId ||
            !account.bankFeesAccountId ||
            !account.interestIncomeAccountId ||
            !account.interestExpenseAccountId
        )).length;
        const neverReconciled = rows.filter(account => !account.lastReconciledDate).length;
        return [
            { label: 'Total accounts', value: rows.length, tone: 'info' },
            { label: 'Reconciliation enabled', value: enabled, tone: 'success' },
            { label: 'Missing GL account', value: missingGl, tone: missingGl ? 'danger' : 'default' },
            { label: 'Missing adjustment accounts', value: missingAdjustment, tone: missingAdjustment ? 'warn' : 'default' },
            { label: 'Never reconciled', value: neverReconciled, tone: neverReconciled ? 'warn' : 'default' }
        ];
    }

    get activeFilterCount(): number {
        return [this.ownerType, this.selectedOwner?.id?.entityId, this.searchText, this.currencyCode, this.reconciliationEnabled !== undefined ? 'recon' : '']
            .filter(Boolean).length;
    }

    get resultText(): string {
        return `${this.filteredAccounts.length} account${this.filteredAccounts.length === 1 ? '' : 's'}`;
    }

    get selectedOwnerLabel(): string {
        return this.selectedOwner ? `${this.selectedOwner.entityName} (${this.selectedOwner.id.entityType})` : 'Select an owner to load bank accounts';
    }

    onOwnerTypeChange(): void {
        this.selectedOwner = undefined;
        if (this.ownerType === PartyType.Company) {
            this.resetDefaultOwner();
            this.loadAccounts();
        }
        this.loadParties();
    }

    searchParties(searchText: string): void {
        this.loadParties(searchText);
    }

    loadParties(searchText = ''): void {
        const companyId = this.companyId;
        if (!companyId) return;
        if (this.ownerType === PartyType.Company && !searchText) {
            this.partyOptions = [this.companyParty()];
            this.selectedOwner = this.selectedOwner ?? this.partyOptions[0];
            return;
        }
        this.partyService.getParties(companyId, [this.ownerType], searchText, 0, 50).subscribe({
            next: page => {
                const parties = page.content ?? [];
                this.partyOptions = this.ownerType === PartyType.Company ? [this.companyParty(), ...parties.filter(party => party.id.entityId !== this.companyId)] : parties;
                this.selectedOwner = this.selectedOwner ?? this.partyOptions[0];
            },
            error: () => {
                this.partyOptions = this.ownerType === PartyType.Company ? [this.companyParty()] : [];
                this.selectedOwner = this.partyOptions[0];
            }
        });
    }

    loadAccounts(): void {
        const ownerId = this.selectedOwner?.id?.entityId;
        if (!ownerId) {
            this.accounts = [];
            return;
        }
        this.loading = true;
        this.bankAccountService.getBankAccounts(this.ownerType, ownerId, this.searchText, 0, 1000).subscribe({
            next: response => {
                this.accounts = response.items ?? [];
                this.totalCount = response.count ?? this.accounts.length;
                this.loading = false;
            },
            error: () => {
                this.accounts = [];
                this.totalCount = 0;
                this.loading = false;
            }
        });
    }

    clearFilters(): void {
        this.searchText = '';
        this.currencyCode = undefined;
        this.reconciliationEnabled = undefined;
        this.ownerType = PartyType.Company;
        this.resetDefaultOwner();
        this.loadParties();
        this.loadAccounts();
    }

    deleteAccount(account: BankAccount): void {
        if (!account.id || !window.confirm(`Delete bank account "${account.accountName}"?`)) return;
        this.bankAccountService.deleteBankAccount(account.id).subscribe(() => this.loadAccounts());
    }

    ownerLabel(account: BankAccount): string {
        return account.party?.entityName || this.selectedOwner?.entityName || `${account.party?.id?.entityType || this.ownerType} #${account.party?.id?.entityId || '-'}`;
    }

    ownerTypeLabel(account: BankAccount): string {
        return account.party?.id?.entityType || this.ownerType;
    }

    private resetDefaultOwner(): void {
        this.selectedOwner = this.companyParty();
        this.partyOptions = [this.selectedOwner];
    }

    private companyParty(): Party {
        const company = this.sessionStorageService.getCompany();
        return {
            id: { entityId: company?.id ?? 0, entityType: PartyType.Company },
            entityName: company?.name || 'Current company',
            emailAddress: company?.emailAddress ?? undefined,
            phoneNumber: company?.phoneNumber ?? undefined,
            imageUrl: company?.logo ?? undefined,
            isCompany: true,
            address: company?.address,
            crNumber: company?.crNumber != null ? String(company.crNumber) : undefined,
            taxRegNumber: company?.taxRegNumber ?? undefined,
            owningCompanyId: company?.id ?? 0
        };
    }

    private get companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private formatOwnerType(value: string): string {
        return value.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
}
